<template>
  <div class="prelab-view d-flex flex-column">
    <!-- Skip Navigation Link -->
    <a href="#prelab-main-content" class="skip-link">Skip to main content</a>
    
    <!-- Header -->
    <header
      class="lab-header d-flex align-items-center justify-content-between flex-wrap gap-2 px-4 py-2 border-bottom bg-white"
    >
      <h5 class="mb-0 fw-bold">
        {{ appMeta.course }} &mdash; Pre-Lab: Pump Characterization
      </h5>
      <div class="d-flex align-items-center gap-2">
        <span
          v-if="isPreLabComplete"
          class="badge bg-success me-2 prelab-complete-badge"
        >
          <i class="bi bi-check-circle me-1" aria-hidden="true"></i>Complete
        </span>
        <button
          class="btn btn-sm btn-outline-secondary"
          :aria-pressed="animationsPaused"
          :aria-label="
            animationsPaused
              ? 'Disable accessibility mode'
              : 'Enable accessibility mode'
          "
          @click="toggleAccessibilityMode"
        >
          <i
            class="bi me-1"
            :class="
              animationsPaused
                ? 'bi-universal-access-circle'
                : 'bi-universal-access'
            "
            aria-hidden="true"
          ></i>
          {{
            animationsPaused
              ? "Accessibility Mode: On"
              : "Accessibility Mode: Off"
          }}
        </button>
        <button
          class="btn btn-sm btn-outline-secondary"
          :aria-pressed="soundsMuted"
          :aria-label="soundsMuted ? 'Unmute sounds' : 'Mute sounds'"
          @click="toggleSounds"
        >
          <i
            class="bi me-1"
            :class="soundsMuted ? 'bi-volume-mute' : 'bi-volume-up'"
            aria-hidden="true"
          ></i>
          {{ soundsMuted ? "Sound: Off" : "Sound: On" }}
        </button>
        <button
          class="btn btn-sm btn-outline-primary"
          :disabled="isPreLabLoading"
          @click="startPreLabTour"
        >
          <i class="bi bi-map me-1" aria-hidden="true"></i>Take Tour
        </button>
      </div>
    </header>

    <div
      v-if="isPreLabLoading"
      class="prelab-loading-wrapper flex-grow-1 d-flex align-items-center justify-content-center p-3"
    >
      <PreLabLoading
        :error-message="datastoreLoadIssue"
        :show-actions="canBypassDatastoreLoad"
        @retry="onRetryDatastoreLoad"
        @continue="onContinueWithoutDatastore"
      />
    </div>

    <!-- Main Content -->
    <div
      v-else
      id="prelab-main-content"
      class="prelab-content flex-grow-1 d-flex flex-column flex-lg-row"
    >
      <!-- Left: Test Stand + hints -->
      <div class="prelab-left flex-grow-1 d-flex flex-column p-3 gap-3">
        <div
          v-if="isLocalOnlyMode"
          class="alert alert-warning mb-0 small"
          role="alert"
        >
          You are working in local-only mode because saved data could not be
          restored. Changes will not sync until restore succeeds.
        </div>

        <!-- Instructions -->
        <div
          id="prelab-tour-objective"
          class="alert alert-primary mb-0 text-start prelab-objective"
        >
          <strong>Objective:</strong> Characterize the pump by measuring suction
          pressure, discharge pressure, and flow rate at different valve
          openings. Calculate pump head for each operating point, then fit the
          data to <em>H<sub>p</sub> = a &minus; b&middot;Q&sup2;</em>.
        </div>

        <div id="prelab-tour-stand">
          <PumpTestStand
            :animations-paused="animationsPaused"
            :sounds-muted="soundsMuted"
            @readings-ready="onReadingsReady"
          />
        </div>

        <DebugHeadCalculator :readings="lastReadings" :fitResult="fitResult" />
      </div>

      <!-- Right: Data Table + Curve Fit -->
      <aside
        class="prelab-sidebar bg-white p-3 text-start"
        aria-label="Data collection and curve fit"
      >
        <!-- Calculation hint -->
        <div class="card mb-3">
          <div class="card-body small text-start">
            <strong>How to calculate pump head (H):</strong>
            <p class="mb-1 mt-1">
              The suction-side transducer (I-1) is open to atmosphere, so P<sub
                >s</sub
              >
              &asymp; 0 psi gauge. Pump head simplifies to:
            </p>
            <p class="mb-0 fw-semibold">
              H = P<sub>d</sub> &times; 144 / &gamma;
            </p>
            <p class="mb-0 text-muted mt-1">
              where &gamma; = 62.3 lbf/ft&sup3; (specific weight of water at
              ~68&nbsp;&deg;F) and P<sub>d</sub> is in psi. The factor 144
              converts in&sup2; to ft&sup2;, giving H in ft.
            </p>
          </div>
        </div>

        <div id="prelab-tour-table">
          <PreLabDataTable ref="dataTable" @data-changed="onDataChanged" />
        </div>

        <hr />

        <!-- Curve Fit Entry -->
        <div id="prelab-tour-fit" class="mt-2">
          <h6 class="fw-semibold text-start">Curve Fit Results</h6>
          <p class="text-muted small mb-2 text-start">
            Using your data, perform a curve fit of the form
            <em>H<sub>p</sub> = a &minus; b&middot;Q&sup2;</em> and enter the
            coefficients and R&sup2; below.
          </p>

          <div class="form-label small fw-semibold mb-1 text-center">
            H<sub>p</sub>(Q) = a &minus; b&middot;Q&sup2;
          </div>
          <div class="row g-2 mb-2">
            <div class="col">
              <label for="userfit-a" class="form-label small mb-0 text-start"
                >a (ft)</label
              >
              <input
                id="userfit-a"
                type="number"
                class="form-control form-control-sm"
                step="0.1"
                v-model.number="userFit.a"
                @blur="normalizeFitField('a')"
                placeholder="e.g. 98.63"
              />
            </div>
            <div class="col">
              <label for="userfit-b" class="form-label small mb-0 text-start"
                >b (ft/GPM&sup2;)</label
              >
              <input
                id="userfit-b"
                type="number"
                class="form-control form-control-sm"
                step="0.0001"
                v-model.number="userFit.b"
                @blur="normalizeFitField('b')"
                placeholder="e.g. 0.0124"
              />
            </div>
          </div>

          <label
            for="userfit-r2"
            class="form-label small fw-semibold mb-0 text-start"
            >R&sup2;</label
          >
          <input
            id="userfit-r2"
            type="number"
            class="form-control form-control-sm mb-2"
            step="0.0001"
            min="0"
            max="1"
            v-model.number="userFit.R2"
            @blur="normalizeFitField('R2')"
            placeholder="e.g. 0.9953"
          />

          <div
            v-if="aFitStatus"
            class="small mt-1 text-start"
            :class="aFitStatus.cls"
            role="status"
          >
            <i :class="aFitStatus.icon" aria-hidden="true"></i>
            {{ aFitStatus.text }}
          </div>

          <div
            v-if="userFitStatus"
            class="small mt-1 text-center"
            :class="userFitStatus.cls"
            role="status"
          >
            <i :class="userFitStatus.icon" aria-hidden="true"></i>
            {{ userFitStatus.text }}
          </div>
          <div
            v-if="userFit.b !== null && userFit.b <= 0"
            class="alert alert-warning small mt-2 py-1 text-start"
            role="alert"
          >
            Negative or zero <em>b</em> coefficient &mdash; double-check your
            calculations.
          </div>
        </div>

        <hr />

        <!-- Proceed button -->
        <button
          id="prelab-tour-proceed"
          class="btn btn-primary w-100"
          @click="onProceedClick"
        >
          <i class="bi bi-arrow-right-circle me-1" aria-hidden="true"></i>
          Save &amp; Proceed to Main Lab
        </button>
        <div v-if="!canProceed" class="text-muted small mt-1 text-start">
          Collect at least 3 complete data points (both Q and H) and enter your
          curve fit coefficients with <em>a</em> &ge; 50 ft and R&sup2; between
          0.80 and 1.0 to proceed.
        </div>
      </aside>
    </div>

    <!-- Requirements reminder modal -->
    <PreLabBlockersModal
      v-if="!isPreLabLoading"
      ref="proceedModal"
      :blockers="proceedBlockers"
    />
  </div>
</template>

<script>
import PumpTestStand from "@/components/activity/PumpTestStand.vue";
import PreLabDataTable from "@/components/activity/PreLabDataTable.vue";
import DebugHeadCalculator from "@/components/activity/DebugHeadCalculator.vue";
import PreLabBlockersModal from "@/components/activity/PreLabBlockersModal.vue";
import PreLabLoading from "@/components/activity/PreLabLoading.vue";
import { fitPumpCurve } from "@/utils/curveFit.js";
import DatastoreAPI from "@/helpers/DatastoreAPI";
import { DebounceError } from "@/utils/debounce.js";
import { M_TO_FT, M3S_TO_GPM } from "@/utils/units.js";
import { mapGetters, mapActions, mapState } from "vuex";
import config from "@/data/config.json";
import Shepherd from "shepherd.js";
import "shepherd.js/dist/css/shepherd.css";
import { buildShepherdSteps, hasSeenTour, markTourSeen } from "@/utils/tour.js";

const PRELAB_LOAD_TIMEOUT_MS = 8000;
const PRELAB_TOUR_SEEN_KEY = "che334.prelabTourSeen.v1";
const ACCESSIBILITY_MODE_PREF_KEY = "che334.accessibilityMode.v1";
const SOUND_MUTED_PREF_KEY = "che334.soundMuted.v1";

export default {
  name: "PreLabView",
  mixins: [DatastoreAPI],
  components: {
    PumpTestStand,
    PreLabDataTable,
    DebugHeadCalculator,
    PreLabBlockersModal,
    PreLabLoading
  },
  data() {
    return {
      appMeta: config.meta,
      dataPoints: [],
      fitResult: null,
      lastReadings: null,
      userFit: { a: null, b: null, R2: null },
      datastoreLoadIssue: "",
      bypassDatastoreLoad: false,
      animationsPaused: false,
      soundsMuted: false,
      loadTimer: null,
      tourDriver: null,
      hasAutoTourStarted: false
    };
  },
  computed: {
    ...mapState("datastore", ["entryLoaded"]),
    ...mapGetters([
      "mapToDatastore",
      "isPreLabComplete",
      "fittedCurve",
      "preLabRows",
      "useAuth",
      "useDatastore"
    ]),
    requiresDatastoreLoad() {
      return this.useAuth && this.useDatastore;
    },
    isPreLabLoading() {
      return (
        this.requiresDatastoreLoad &&
        !this.entryLoaded &&
        !this.bypassDatastoreLoad
      );
    },
    canBypassDatastoreLoad() {
      return this.requiresDatastoreLoad && !this.entryLoaded;
    },
    canShowPreLabContent() {
      return !this.isPreLabLoading;
    },
    isLocalOnlyMode() {
      return (
        this.requiresDatastoreLoad &&
        this.bypassDatastoreLoad &&
        !this.entryLoaded
      );
    },
    preLabRowStats() {
      const rows = Array.isArray(this.preLabRows) ? this.preLabRows : [];
      let valid = 0;
      const incompleteRows = [];

      rows.forEach((row, index) => {
        const hasQ = Number.isFinite(row?.Q) && row.Q > 0;
        const hasH = Number.isFinite(row?.H) && row.H > 0;

        if (hasQ && hasH) {
          valid += 1;
        } else if (hasQ || hasH) {
          incompleteRows.push(index + 1);
        }
      });

      return {
        total: rows.length,
        valid,
        incompleteRows
      };
    },
    userFitStatus() {
      if (this.userFit.R2 === null || this.userFit.R2 === "") return null;
      if (this.userFit.R2 > 1)
        return {
          cls: "text-danger",
          icon: "bi bi-x-circle me-1",
          text: "R² cannot exceed 1.0 — double-check your value."
        };
      if (this.userFit.R2 >= 0.95)
        return {
          cls: "text-success",
          icon: "bi bi-check-circle me-1",
          text: "Good fit"
        };
      if (this.userFit.R2 >= 0.8)
        return {
          cls: "text-warning",
          icon: "bi bi-exclamation-triangle me-1",
          text: "Fair fit"
        };
      return {
        cls: "text-danger",
        icon: "bi bi-x-circle me-1",
        text: "Poor fit — aim for R² ≥ 0.80"
      };
    },
    aFitStatus() {
      const a = this.userFit.a;
      if (a === null || a === "" || !Number.isFinite(Number(a))) return null;
      if (a <= 0)
        return {
          cls: "text-danger",
          icon: "bi bi-x-circle me-1",
          text: "Shutoff head must be positive."
        };
      if (a < 50)
        return {
          cls: "text-danger",
          icon: "bi bi-x-circle me-1",
          text: `Shutoff head (${a} ft) is too low — likely a unit error. Expected ~80–120 ft for this pump.`
        };
      if (a < 75)
        return {
          cls: "text-warning",
          icon: "bi bi-exclamation-triangle me-1",
          text: `Shutoff head (${a} ft) is lower than expected (~80–120 ft). Double-check your calculations.`
        };
      return null;
    },
    proceedValidation() {
      const hasEnoughData = this.preLabRowStats.valid >= 3;
      const hasValidFit = this.hasValidFit();
      const isAlreadyComplete = this.isPreLabComplete && !!this.fittedCurve;
      const blockers = this.buildProceedBlockers({
        hasEnoughData,
        isAlreadyComplete
      });

      return {
        hasEnoughData,
        hasValidFit,
        isAlreadyComplete,
        canProceed: isAlreadyComplete || (hasEnoughData && hasValidFit),
        blockers
      };
    },
    canProceed() {
      return this.proceedValidation.canProceed;
    },
    proceedBlockers() {
      return this.proceedValidation.blockers;
    }
  },
  watch: {
    mapToDatastore(val) {
      if (this.isPreLabLoading || this.isLocalOnlyMode) return;
      this.updateEntry(val).catch((e) => {
        if (!(e instanceof DebounceError)) console.error(e);
      });
    },
    entryLoaded(loaded) {
      if (!loaded) return;
      this.datastoreLoadIssue = "";
      this.clearLoadTimer();
    },
    canShowPreLabContent(canShow) {
      if (!canShow) return;
      this.maybeStartTourOnce();
    },
    fittedCurve: {
      immediate: true,
      handler(curve) {
        if (!curve) return;
        // Convert stored SI values back to English units for display
        this.userFit = {
          a: Number.isFinite(curve.a)
            ? parseFloat((curve.a * M_TO_FT).toFixed(2))
            : null,
          b: Number.isFinite(curve.b)
            ? parseFloat(
                ((curve.b * M_TO_FT) / (M3S_TO_GPM * M3S_TO_GPM)).toFixed(6)
              )
            : null,
          R2: Number.isFinite(curve.R2) ? curve.R2 : null
        };
      }
    }
  },
  created() {
    this.restorePreferences();
  },
  mounted() {
    this.ensureDatastoreReady();
    this.maybeStartTourOnce();
  },
  beforeUnmount() {
    this.clearLoadTimer();
    this.tourDriver?.cancel();
  },
  methods: {
    ...mapActions({
      setFittedCurve: "setFittedCurve",
      completePreLabAction: "completePreLab"
    }),
    handleStorageError(action, error) {
      // Non-blocking: storage access can fail in private mode or restricted browsers.
      console.warn(`Failed to ${action} accessibility preference.`, error);
    },
    hasValidFit() {
      return (
        Number.isFinite(this.userFit.a) &&
        this.userFit.a >= 50 &&
        Number.isFinite(this.userFit.b) &&
        this.userFit.b > 0 &&
        Number.isFinite(this.userFit.R2) &&
        this.userFit.R2 >= 0.8 &&
        this.userFit.R2 <= 1
      );
    },
    buildIncompleteDataBlocker() {
      let msg =
        `At least 3 valid data points are required (you have ${this.preLabRowStats.valid}). ` +
        "A valid point must include both Q and H values greater than 0.";
      if (this.preLabRowStats.incompleteRows.length > 0) {
        const rows = this.preLabRowStats.incompleteRows.join(", ");
        msg += ` Incomplete row(s): ${rows}.`;
      }
      return msg;
    },
    buildProceedBlockers({ hasEnoughData, isAlreadyComplete }) {
      if (isAlreadyComplete) return [];

      const blockers = [];
      if (!hasEnoughData) {
        blockers.push(this.buildIncompleteDataBlocker());
      }
      if (!Number.isFinite(this.userFit.a) || this.userFit.a < 50) {
        blockers.push("Enter a shutoff head coefficient a ≥ 50 ft.");
      }
      if (!Number.isFinite(this.userFit.b) || this.userFit.b <= 0) {
        blockers.push("Enter a positive b coefficient (ft/GPM²).");
      }
      if (
        !Number.isFinite(this.userFit.R2) ||
        this.userFit.R2 < 0.8 ||
        this.userFit.R2 > 1
      ) {
        const r2 = Number.isFinite(this.userFit.R2)
          ? ` (yours is ${this.userFit.R2})`
          : "";
        blockers.push(`R² must be between 0.80 and 1.0${r2}.`);
      }

      return blockers;
    },
    prefersReducedMotion() {
      return (
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      );
    },
    restorePreferences() {
      try {
        const stored = window.localStorage.getItem(ACCESSIBILITY_MODE_PREF_KEY);
        this.animationsPaused =
          stored === null ? this.prefersReducedMotion() : stored === "true";
      } catch (e) {
        this.handleStorageError("read", e);
        this.animationsPaused = this.prefersReducedMotion();
      }
      try {
        this.soundsMuted =
          window.localStorage.getItem(SOUND_MUTED_PREF_KEY) === "true";
      } catch (e) {
        this.handleStorageError("read", e);
        this.soundsMuted = false;
      }
    },
    persistAnimationsPreference() {
      try {
        window.localStorage.setItem(
          ACCESSIBILITY_MODE_PREF_KEY,
          String(this.animationsPaused)
        );
      } catch (e) {
        this.handleStorageError("save", e);
      }
    },
    persistSoundPreference() {
      try {
        window.localStorage.setItem(
          SOUND_MUTED_PREF_KEY,
          String(this.soundsMuted)
        );
      } catch (e) {
        this.handleStorageError("save", e);
      }
    },
    toggleAccessibilityMode() {
      this.animationsPaused = !this.animationsPaused;
      this.persistAnimationsPreference();
    },
    toggleSounds() {
      this.soundsMuted = !this.soundsMuted;
      this.persistSoundPreference();
    },
    ensureDatastoreReady() {
      if (!this.isPreLabLoading) return;

      this.datastoreLoadIssue = "";
      this.startLoadTimer();

      this.readEntry(() => {
        this.clearLoadTimer();
        if (!this.entryLoaded && !this.bypassDatastoreLoad) {
          this.datastoreLoadIssue =
            "We could not restore your saved data right now. Retry, or continue without restoring.";
        }
      });
    },
    startLoadTimer() {
      this.clearLoadTimer();
      this.loadTimer = setTimeout(() => {
        if (this.entryLoaded || this.bypassDatastoreLoad) return;
        this.datastoreLoadIssue =
          "Loading is taking longer than expected. You can retry, or continue without restoring.";
      }, PRELAB_LOAD_TIMEOUT_MS);
    },
    clearLoadTimer() {
      if (!this.loadTimer) return;
      clearTimeout(this.loadTimer);
      this.loadTimer = null;
    },
    onRetryDatastoreLoad() {
      this.ensureDatastoreReady();
    },
    onContinueWithoutDatastore() {
      this.bypassDatastoreLoad = true;
      this.clearLoadTimer();
    },
    startPreLabTour() {
      if (this.isPreLabLoading) return false;

      this.tourDriver?.cancel();
      this.tourDriver = new Shepherd.Tour({
        useModalOverlay: true,
        exitOnEsc: true,
        keyboardNavigation: true,
        defaultStepOptions: {
          canClickTarget: false,
          cancelIcon: { enabled: true },
          scrollTo: { behavior: "smooth", block: "center" }
        }
      });

      const steps = buildShepherdSteps(
        [
          {
            selector: "#prelab-tour-objective",
            description:
              "Start here: this objective explains what measurements and calculations you will complete in pre-lab.",
            title: "Pre-Lab Objective"
          },
          {
            selector: "#prelab-tour-stand",
            description:
              "Use the virtual test stand to toggle the pump, adjust the valve, and collect pressure/flow readings.",
            title: "Pump Test Stand"
          },
          {
            selector: "#prelab-tour-table",
            description:
              "Enter each valid operating point here. A valid point requires both Q and H values greater than zero.",
            title: "Data Collection Table"
          },
          {
            selector: "#prelab-tour-fit",
            description:
              "After collecting data, enter your curve-fit coefficients and R². You need a >= 50, b > 0, and R² between 0.80 and 1.0.",
            title: "Curve Fit Results"
          },
          {
            selector: "#prelab-tour-proceed",
            description:
              "When requirements are met, save your work and proceed to the main lab activity.",
            title: "Proceed to Main Lab"
          }
        ],
        this.tourDriver
      );

      if (steps.length === 0) {
        this.tourDriver.cancel();
        return false;
      }

      this.tourDriver.addSteps(steps);
      try {
        this.tourDriver.start();
        return true;
      } catch (e) {
        console.error("Failed to start pre-lab tour", e);
        return false;
      }
    },
    maybeStartTourOnce() {
      if (this.isPreLabLoading || this.hasAutoTourStarted) return;
      if (hasSeenTour(PRELAB_TOUR_SEEN_KEY)) return;

      this.$nextTick(() => {
        const didStart = this.startPreLabTour();
        if (!didStart) return;

        this.hasAutoTourStarted = true;
        this.markTourSeen();
      });
    },
    markTourSeen() {
      markTourSeen(PRELAB_TOUR_SEEN_KEY);
    },
    onReadingsReady(readings) {
      this.lastReadings = readings;
    },
    normalizeFitField(field) {
      const raw = this.userFit[field];
      if (raw === null || raw === undefined || raw === "") {
        this.userFit[field] = null;
        return;
      }
      if (typeof raw === "string") {
        const trimmed = raw.trim();
        if (!trimmed) {
          this.userFit[field] = null;
          return;
        }
        const parsed = Number(trimmed);
        this.userFit[field] = Number.isFinite(parsed) ? parsed : null;
        return;
      }
      this.userFit[field] = Number.isFinite(raw) ? raw : null;
    },
    onDataChanged(validData) {
      this.dataPoints = validData;
      this.fitResult = fitPumpCurve(validData);
    },
    onProceedClick() {
      if (!this.canProceed) {
        this.$refs.proceedModal.show();
        return;
      }
      this.completePreLab();
    },
    completePreLab() {
      const { canProceed, hasValidFit } = this.proceedValidation;
      if (!canProceed) return;
      if (hasValidFit) {
        // Convert student's English-unit fit (ft, GPM) to SI (m, m³/s)
        // so the simulation engine receives the expected units.
        const FT_TO_M = 1 / 3.28084;
        const GPM_TO_M3S = 1 / 15850.3;
        this.setFittedCurve({
          a: this.userFit.a * FT_TO_M,
          b: (this.userFit.b * FT_TO_M) / (GPM_TO_M3S * GPM_TO_M3S),
          R2: this.userFit.R2
        });
      }
      this.completePreLabAction();
      if (!this.isLocalOnlyMode) {
        this.updateEntry(this.mapToDatastore).catch((e) => {
          if (!(e instanceof DebounceError)) console.error(e);
        });
      }
      this.$router.push({ name: "Activity" });
    }
  }
};
</script>

<style scoped>
.prelab-view {
  background-color: #f8f9fa;
  min-height: 100vh;
}
.lab-header {
  z-index: 10;
}

.prelab-objective {
  font-size: 1rem;
  line-height: 1.5;
}

.prelab-complete-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.875rem;
  line-height: 1.5;
  padding: 0.25rem 0.5rem;
  border-radius: 0.2rem;
}

/* Desktop: restore fixed-height, side-by-side scrollable panels */
@media (min-width: 992px) {
  .prelab-view {
    height: 100vh;
    overflow: hidden;
  }
  .prelab-content {
    overflow: hidden;
  }
  .prelab-left {
    overflow-y: auto;
  }
  .prelab-sidebar {
    overflow-y: auto;
  }
}
</style>
